"use client"

import Link from 'next/link'
import { useMemo, useState } from 'react'
import Section from '@components/layout/Section'
import Maybe from '@components/shared/Maybe'
import { DragInput } from '@components/payment/DragInput'
import Button from '@components/button/Button'
import Icon from '@components/shared/Icon'
import offerPhoto from '@utils/links/offerPhoto'
import offerLinkSlugified from '@utils/links/offerLinkSlugified'
import useMediaQueryBreakpoint from '@hooks/useMediaQueryBreakpoint'
import useT from '@hooks/useTranslation'

import styles from './Comperia.module.css'

const shouldView = (offer) => ['car_ad','truck_ad','motorcycle_ad','ciezarowe'].includes(offer?.type)

const CONFIG = {
	btnStyle: {
		fontWeight: 'bold',
		margin: 'auto',
	},
	host: process.env.NEXT_PUBLIC_HOST,
	imgSrc: '/assets/icons/comperia-logo-w.png',
	imgStyle: {
		display: 'block',
		maxWidth: 154,
		width: '100%',
	},
	imgMobileStyle: {
		display: 'block',
		margin: 'auto',
		marginBottom: 20,
	},
	textStyle: {
		color: '#fff',
		textAlign: 'center',
	},
	url: `https://form.comperiaraty.pl/samochody-pl-finansowanie-ratalne-k1
        ?utm_source=samochody.pl
        &utm_medium=display
        &utm_campaign=kredyty_gotowkowe
        &utm_term=karta_pojazdu_sekcja_szczegoly
        &utm_content=tekst
        &price=__price__
        &pozyczka=__loan__
        &title=__title__
        &img=__img__
        &model=__model__
        &marka=__make__
        &link=__url__
        &okres=__period__`
		.replace(/\s+/g, '')
		.trim(),
	wrapperStyle: {
		background: 'var(--teal)',
		borderRadius: 'var(--border-radius)',
		color: '#fff',
	},
}

function Comperia(props) {
	return null
	return (
		<Maybe empty={<br/>} test={shouldView(props.offer)}>
			<_Comperia {...props} />
		</Maybe>
	)
}

function _Comperia({ offer }) {
	const [period, setPeriod] = useState(60)
	const { make, model } = offer || {}

	const onMobile = useMediaQueryBreakpoint(800)
	const onMobile2 = useMediaQueryBreakpoint(400)

	const href = useComperiaUrl(offer, period)

	const inject = (
		<span
			style={{
				...CONFIG.textStyle,
				...(onMobile
					? { display: 'block', marginBottom: 16, marginTop: 30 }
					: {}),
			}}
		>
			<b style={CONFIG.textStyle}>{useT('Okres spłaty')}</b> (
			{useT('miesiące')}):
		</span>
	)

	return (
		<>
			<br />
			<h2>
				{useT('Oblicz ratę')} {make} {model}
			</h2>
			<Section.Box
				style={{
					...CONFIG.wrapperStyle,
					...(onMobile2 ? { marginInline: 0 } : {}),
				}}
			>
				<div className={styles.cwrap}>
					<img
						alt="Comperia"
						src={CONFIG.imgSrc}
						style={onMobile ? CONFIG.imgMobileStyle : CONFIG.imgStyle}
					/>
					{onMobile && inject}
					<DragInput
						amount={60}
						breakpoint={800}
						className={styles.cinput}
						inputClass={styles.input}
						min={1}
						max={120}
						inject={onMobile ? undefined : inject}
						onChange={setPeriod}
						progressStyle={{ background: '#fff' }}
						step={1}
						unit=""
					/>
					{onMobile && <br />}
					<Button
						href={href}
						style={CONFIG.btnStyle}
						target="_blank"
						white
					>
						{useT('Oblicz ratę')}
					</Button>
				</div>
			</Section.Box>
			<br />
		</>
	)
}

function ComperiaUrl ({ condition = true, offer, text = 'Kup na raty' }) {
	const href = useComperiaUrl(offer, 120)
	return (
		<Maybe test={condition && shouldView(offer)}>
			<div style={{marginBottom:20,marginTop:-16,textDecoration:'underline'}}>
				<Link href={href} target="_blank">
					{useT(text)}
				</Link>
				<Icon>arrow_right_alt</Icon>
			</div>
		</Maybe>
	)
}

function ComperiaWrapperWithButton ({ children, condition = true, offer, text = 'Kup na raty' }) {
	const href = useComperiaUrl(offer, 120)
	return (
		<div className={styles['btn-wrap']}>
			{children}
			<Maybe test={condition && shouldView(offer)}>
				<Button href={href} target="_blank" teal>{useT(text)}<Icon>arrow_right_alt</Icon></Button>
			</Maybe>
		</div>
	)
}

function FinancingLink ({ children, condition, href, text = 'Sprawdź finansowanie' }) {
	const test = Boolean(condition && href)
	return (
		<div className={styles['btn-wrap']}>
			{children}
			<Maybe test={test}>
				<Button href={href} rel="nofollow" target="_blank" style={{background:'#fff', border: '1px solid var(--teal)', color: 'var(--teal)'}}>
					{useT(text)}<Icon>arrow_right_alt</Icon>
				</Button>
			</Maybe>
		</div>
	)
}

function useComperiaUrl (offer, period) {
	const { make, model, price } = offer || {}
	const { price: priceValue } = price || {}

	const href = useMemo(
		() =>
			CONFIG.url
				.replace('__price__', priceValue)
				.replace('__loan__', priceValue)
				.replace('__title__', [make, model].filter(Boolean).join(' '))
				.replace('__img__', offerPhoto(offer, 0, 'x1view'))
				.replace('__model__', model)
				.replace('__make__', make)
				.replace('__url__', CONFIG.host + offerLinkSlugified(offer))
				.replace('__period__', period),
		[make, model, offer, period, priceValue],
	)

	return href
}

export {
	Comperia,
	ComperiaUrl,
	ComperiaWrapperWithButton,
	FinancingLink,
}
